import * as React from "react";
import { FormControl, FormLabel } from "@mui/joy";
import { Editor } from "primereact/editor";
import { Dropdown } from "primereact/dropdown";
import { dropdownViewGet } from "../service/api-service";
import { useEffect } from "react";
import { getDocumentPlaceholders } from "../service/documentPlaceholders";
import { useToast } from "../context/ToastContext";
import { Select } from "./Shared/Forms";

interface inputPrams {
  label: string;
  content?: string;
  setContent: React.Dispatch<string>;
  required?: boolean;
  heightPx?: number;
  toast?: any;
  hidePlaceholder?: boolean;
  disabled?: boolean;
  error?: string;
  onFocus?: () => void;
  onPlaceholdersLoaded?: () => void;
}

export const QuillEditor = React.forwardRef((params: inputPrams, ref) => {
  const [selectedData, setSelectedData] = React.useState(null);
  const [placeholders, setPlaceholders] = React.useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [editorInstance, setEditorInstance] = React.useState<any>(null);
  const [lastCursorPosition, setLastCursorPosition] = React.useState<number>(0);
  const toast = useToast();

  React.useImperativeHandle(ref, () => ({
    validate() {
      if (!params.content) return false;

      // @ts-ignore
      const matchedPlaceholders = [...params.content.matchAll(/\{([^}]+)\}/g)].map(m => m[1]);
      let hasPlaceholderErrors = [];
      let hasOneSignature = false;

      if (matchedPlaceholders.length > 0) {
        for (let placeholder of matchedPlaceholders) {
          if (placeholder.startsWith("appointment__") && placeholder.endsWith("__signature")) {
            hasOneSignature = true;
          }
          if (placeholders.filter(item => item.value === `{${placeholder}}`).length === 0) {
            hasPlaceholderErrors.push(placeholder);
          }
        }
      }
      return {
        hasOneSignature,
        hasPlaceholderErrors,
      };
    },
    insertHtmlAtCursor(html: string) {
      if (editorInstance) {
        // Get current selection or use last known position
        const selection = editorInstance.getSelection() || { index: lastCursorPosition, length: 0 };
        const insertPosition = selection.index;
        
        // Check if we're in the middle of a line
        const currentLine = editorInstance.getLine(insertPosition);
        const lineStart = insertPosition - (currentLine?.[1] || 0);
        
        // If we're not at the start of a line, add a line break first
        if (currentLine?.[1] > 0) {
          editorInstance.insertText(insertPosition, '\n', 'user');
          // Insert the HTML after the line break
          editorInstance.clipboard.dangerouslyPasteHTML(insertPosition + 1, html);
          // Add another line break after to separate from following content
          editorInstance.insertText(insertPosition + 1 + html.length, '\n', 'user');
          // Set cursor after the inserted content
          editorInstance.setSelection(insertPosition + 2 + html.length, 0);
          setLastCursorPosition(insertPosition + 2 + html.length);
        } else {
          // We're at the start of a line, just insert normally
          editorInstance.clipboard.dangerouslyPasteHTML(insertPosition, html);
          // Add a line break after to separate from following content
          editorInstance.insertText(insertPosition + html.length, '\n', 'user');
          // Set cursor after the inserted content
          editorInstance.setSelection(insertPosition + html.length + 1, 0);
          setLastCursorPosition(insertPosition + html.length + 1);
        }
      }
    },
    getEditorInstance() {
      return editorInstance;
    }
  }));

  // Track cursor position changes
  React.useEffect(() => {
    if (editorInstance) {
      const handleSelectionChange = (range: any) => {
        if (range) {
          setLastCursorPosition(range.index);
        }
      };

      editorInstance.on('selection-change', handleSelectionChange);

      return () => {
        editorInstance.off('selection-change', handleSelectionChange);
      };
    }
  }, [editorInstance]);

  useEffect(() => {
    if (params.disabled || params.hidePlaceholder) return;
    loadAllSignatures();
  }, []);

  async function loadAllSignatures() {
    setLoading(true);
    try {
      const data = await getDocumentPlaceholders();
      setPlaceholders(data.content);
      params.onPlaceholdersLoaded?.();
    } finally {
      setLoading(false);
    }
  }

  const placeholderSelected = (e: any) => {
    const value = e.value;
    const name = placeholders.find(item => item.value === value);
    setSelectedData(e.value);
    navigator.clipboard.writeText(e.value);
    toast("success", `${name?.label} Copied to Clipboard`, 1000);
  };

  return (
    <div>
      {!params.hidePlaceholder && (
        <>
          <Select
            options={placeholders}
            onChange={placeholderSelected}
            label={params.label}
            required={params.required}
            placeholder={"Select to copy placeholder to clipboard"}
            showClear
            disabled={params.disabled}
            optionLabel={"label"}
            optionValue={"value"}
            filter
          />
        </>
      )}

      <br />
      <Editor
        readOnly={params.disabled}
        value={params.content}
        onTextChange={e => params.setContent(e.htmlValue || "")}
        onLoad={(quill) => setEditorInstance(quill)}
        onFocus={params.onFocus}
        style={{
          height: `${params?.heightPx || 30}vh`,
          border: params.error ? "1px solid #e24c4c" : "1px solid #ccc",
          borderBottomLeftRadius: "0.5rem",
          borderBottomRightRadius: "0.5rem",
        }}
      />
      {params.error && <small className="p-error block mt-1">{params.error}</small>}
    </div>
  );
});
